import React, { useEffect, useState } from 'react';
import {
  Paper, Typography, Button, IconButton, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Stack, Dialog, DialogTitle, DialogContent, DialogActions, TextField, FormControl, InputLabel, Select, MenuItem, Pagination, Box, Card, CardContent, Divider
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import BusinessIcon from '@mui/icons-material/Business';
import ContactPhoneIcon from '@mui/icons-material/ContactPhone';
import EmailIcon from '@mui/icons-material/Email';
import AddIcon from '@mui/icons-material/Add';

const initialState = { name: '', contact: '', address: '', email: '', phone: '', notes: '' };


const DealerList = ({ token }) => {
  const [dealers, setDealers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [formOpen, setFormOpen] = useState(false);
  const [editDealer, setEditDealer] = useState(null);
  const [form, setForm] = useState(initialState);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);

  const fetchDealers = async () => {
    setLoading(true);
    const res = await fetch('/api/dealers', { headers: { Authorization: `Bearer ${token}` } });
    const data = await res.json();
    setDealers(Array.isArray(data) ? data : []);
    setLoading(false);
  };

  useEffect(() => { if (token) fetchDealers(); }, [token]);

  const handleOpen = (dealer) => {
    setEditDealer(dealer);
    setForm(dealer ? dealer : initialState);
    setFormOpen(true);
  };
  const handleClose = () => setFormOpen(false);

  const handleChange = e => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSave = async () => {
    const method = editDealer ? 'PUT' : 'POST';
    const url = editDealer ? `/api/dealers/${editDealer._id}` : '/api/dealers';
    await fetch(url, {
      method,
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify(form)
    });
    setFormOpen(false);
    fetchDealers();
  };

  const handleDelete = async (dealer) => {
    if (!window.confirm('Delete this dealer?')) return;
    await fetch(`/api/dealers/${dealer._id}`, {
      method: 'DELETE',
      headers: { Authorization: `Bearer ${token}` }
    });
    fetchDealers();
  };

  // Filter dealers by search

  const filteredDealers = dealers.filter(dealer => {
    const q = search.trim().toLowerCase();
    if (!q) return true;
    return (
      (dealer.name && dealer.name.toLowerCase().includes(q)) ||
      (dealer.contact && dealer.contact.toLowerCase().includes(q)) ||
      (dealer.address && dealer.address.toLowerCase().includes(q)) ||
      (dealer.email && dealer.email.toLowerCase().includes(q)) ||
      (dealer.phone && dealer.phone.toLowerCase().includes(q))
    );
  });

  // Pagination logic
  const totalPages = Math.ceil(filteredDealers.length / pageSize) || 1;
  const pagedDealers = filteredDealers.slice((page - 1) * pageSize, page * pageSize);

  // Reset to page 1 if search or pageSize changes
  useEffect(() => { setPage(1); }, [search, pageSize]);

  return (
    <Box sx={{ display: 'flex', height: 'calc(100vh - 64px)', bgcolor: '#f0f2f5' }}>
      {/* Left Panel - Dealers List */}
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        {/* Header */}
        <Box sx={{ p: 2, bgcolor: 'white', borderBottom: '2px solid #e0e0e0', display: 'flex', alignItems: 'center', gap: 2 }}>
          <LocalShippingIcon sx={{ fontSize: 32, color: 'primary.main' }} />
          <Box sx={{ flex: 1 }}>
            <Typography variant="h5" fontWeight={700}>
              Dealers
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {filteredDealers.length} {filteredDealers.length === 1 ? 'dealer' : 'dealers'} registered
            </Typography>
          </Box>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpen(null)}>
            Add Dealer
          </Button>
        </Box>

        {/* Search Bar */}
        <Box sx={{ p: 2, bgcolor: 'white', borderBottom: '1px solid #e0e0e0' }}>
          <Stack direction="row" spacing={2} alignItems="center">
            <TextField
              label="Search Dealers"
              placeholder="Search by name, contact, email, or phone..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              size="small"
              sx={{ flex: 1 }}
            />
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel id="page-size-label">Show</InputLabel>
              <Select
                labelId="page-size-label"
                value={pageSize}
                label="Show"
                onChange={e => setPageSize(Number(e.target.value))}
              >
                <MenuItem value={25}>25</MenuItem>
                <MenuItem value={50}>50</MenuItem>
                <MenuItem value={100}>100</MenuItem>
              </Select>
            </FormControl>
          </Stack>
        </Box>

        {/* Table */}
        <Box sx={{ flex: 1, overflow: 'auto', bgcolor: 'white' }}>
          <TableContainer>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Contact</TableCell>
                  <TableCell>Address</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Phone</TableCell>
                  <TableCell>Notes</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center">
                      <Typography variant="body2" color="text.secondary" py={4}>
                        Loading...
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : pagedDealers.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center">
                      <Typography variant="body2" color="text.secondary" py={4}>
                        No dealers found
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  pagedDealers.map((dealer) => (
                    <TableRow key={dealer._id} hover>
                      <TableCell>
                        <Typography variant="body2" fontWeight={600}>{dealer.name}</Typography>
                      </TableCell>
                      <TableCell>{dealer.contact || '—'}</TableCell>
                      <TableCell sx={{ maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {dealer.address || '—'}
                      </TableCell>
                      <TableCell>{dealer.email || '—'}</TableCell>
                      <TableCell>{dealer.phone || '—'}</TableCell>
                      <TableCell sx={{ maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {dealer.notes || '—'}
                      </TableCell>
                      <TableCell align="right">
                        <IconButton size="small" color="primary" onClick={() => handleOpen(dealer)}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton size="small" color="error" onClick={() => handleDelete(dealer)}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>

        {/* Pagination */}
        {filteredDealers.length > 0 && (
          <Box sx={{ p: 2, bgcolor: 'white', borderTop: '1px solid #e0e0e0', display: 'flex', justifyContent: 'center' }}>
            <Pagination
              count={totalPages}
              page={page}
              onChange={(_, value) => setPage(value)}
              color="primary"
              shape="rounded"
              showFirstButton
              showLastButton
            />
          </Box>
        )}
      </Box>

      {/* Right Panel - Summary */}
      <Paper 
        elevation={3}
        sx={{ 
          width: { xs: '100%', md: 400 }, 
          display: 'flex', 
          flexDirection: 'column',
          maxHeight: '100%',
          overflow: 'auto'
        }}
      >
        {/* Summary Header */}
        <Box sx={{ p: 2, bgcolor: 'primary.main', color: 'white' }}>
          <Typography variant="subtitle2" fontWeight={600}>
            SUMMARY
          </Typography>
        </Box>

        <Box sx={{ p: 3 }}>
          {/* Statistics Cards */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 3 }}>
            {/* Total Dealers */}
            <Card sx={{ boxShadow: 2, border: '2px solid', borderColor: 'primary.main' }}>
              <CardContent sx={{ p: 2.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
                  <Box sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: 36,
                    height: 36,
                    borderRadius: 1,
                    bgcolor: 'primary.lighter',
                    color: 'primary.main'
                  }}>
                    <BusinessIcon sx={{ fontSize: 20 }} />
                  </Box>
                  <Typography variant="caption" color="text.secondary" fontWeight={500} textTransform="uppercase">
                    Total Dealers
                  </Typography>
                </Box>
                <Typography variant="h3" fontWeight={700} color="primary.main">
                  {dealers.length}
                </Typography>
              </CardContent>
            </Card>

            {/* Dealers with Contact */}
            <Card sx={{ boxShadow: 1 }}>
              <CardContent sx={{ p: 2.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
                  <Box sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: 36,
                    height: 36,
                    borderRadius: 1,
                    bgcolor: 'success.lighter',
                    color: 'success.main'
                  }}>
                    <ContactPhoneIcon sx={{ fontSize: 20 }} />
                  </Box>
                  <Typography variant="caption" color="text.secondary" fontWeight={500} textTransform="uppercase">
                    With Phone
                  </Typography>
                </Box>
                <Typography variant="h3" fontWeight={700} color="text.primary">
                  {dealers.filter(d => d.phone).length}
                </Typography>
              </CardContent>
            </Card>

            {/* Dealers with Email */}
            <Card sx={{ boxShadow: 1 }}>
              <CardContent sx={{ p: 2.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
                  <Box sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: 36,
                    height: 36,
                    borderRadius: 1,
                    bgcolor: 'info.lighter',
                    color: 'info.main'
                  }}>
                    <EmailIcon sx={{ fontSize: 20 }} />
                  </Box>
                  <Typography variant="caption" color="text.secondary" fontWeight={500} textTransform="uppercase">
                    With Email
                  </Typography>
                </Box>
                <Typography variant="h3" fontWeight={700} color="text.primary">
                  {dealers.filter(d => d.email).length}
                </Typography>
              </CardContent>
            </Card>
          </Box>

          <Divider sx={{ my: 3 }} />

          {/* Info Box */}
          <Card sx={{ bgcolor: 'info.lighter', boxShadow: 0 }}>
            <CardContent sx={{ p: 2 }}>
              <Typography variant="body2" color="info.dark" fontWeight={500} gutterBottom>
                💡 Quick Tips
              </Typography>
              <Box component="ul" sx={{ m: 0, pl: 2.5, color: 'text.secondary' }}>
                <Typography component="li" variant="caption" sx={{ mb: 0.5 }}>
                  Dealers are suppliers who provide products
                </Typography>
                <Typography component="li" variant="caption" sx={{ mb: 0.5 }}>
                  Add contact information for easy communication
                </Typography>
                <Typography component="li" variant="caption">
                  Use notes to track special terms or agreements
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Box>
      </Paper>

      {/* Dialog */}
      <Dialog open={formOpen} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>{editDealer ? 'Edit Dealer' : 'Add Dealer'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} mt={1}>
            <TextField label="Name" name="name" value={form.name} onChange={handleChange} required fullWidth />
            <TextField label="Contact" name="contact" value={form.contact} onChange={handleChange} fullWidth />
            <TextField label="Address" name="address" value={form.address} onChange={handleChange} fullWidth />
            <TextField label="Email" name="email" value={form.email} onChange={handleChange} fullWidth />
            <TextField label="Phone" name="phone" value={form.phone} onChange={handleChange} fullWidth />
            <TextField label="Notes" name="notes" value={form.notes} onChange={handleChange} fullWidth />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
          <Button onClick={handleSave} variant="contained">Save</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DealerList;
